import { useMemo } from 'react'
import camelCase from 'lodash/camelCase'
import {
  FieldValues,
  useController,
  UseControllerProps,
  useForm,
} from 'react-hook-form'
import { Select } from '.'
import { Input } from '../Input'

export type SelectOption = {
  id: string
  value: string
  title: string
  subtitle?: string
}

const randomizeOptions = (options: SelectOption[]) =>
  options.sort((a, b) => {
    //we keep 'Other' at the end and randomize the others
    if (a.id === 'other') {
      return 1
    } else if (b.id === 'other') {
      return -1
    }
    return Math.random() - 0.5
  })

type SelectProps = {
  options: SelectOption[]
  placeholder: string
  margin: 'both'
}
export type ControlledSelectProps<T extends FieldValues> = SelectProps &
  UseControllerProps<T> & {
    randomize?: boolean
    other?: {
      placeholder: string
      className?: string
    }
  }

export function ControlledSelect<T extends FieldValues>({
  name,
  control,
  rules,
  options,
  margin,
  placeholder,
  other,
  randomize = false,
}: ControlledSelectProps<T>) {
  const { field: selectField } = useController({
    name,
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
        notEmpty: (o) => (o as SelectOption)?.value !== '',
      },
    },
    control,
  })
  const value = selectField.value as SelectOption | undefined
  const isOther = value?.id === 'other'

  const otherFieldMethods = useForm<{ other: string }>({
    mode: 'onChange',
    defaultValues: { other: '', [name]: undefined },
    shouldUnregister: true,
  })

  const {
    field: { onChange, ...field },
    fieldState,
  } = useController({
    name: 'other',
    control: otherFieldMethods.control,
    rules: { required: true },
  })

  const orderedOptions = useMemo(
    () => (randomize ? randomizeOptions(options) : options),
    [options, randomize]
  )

  return (
    <>
      <Select
        id={name}
        placeholder={placeholder}
        margin={margin}
        {...selectField}
        options={orderedOptions}
        optionId={value?.id}
        data-test-id={camelCase(`${name} select`)}
      />
      {other && isOther && (
        <Input
          placeholder={other.placeholder}
          className="margin-vertical-8"
          autoFocus
          {...field}
          onChange={(e) => {
            selectField.onChange({
              ...e,
              target: {
                ...e.target,
                value: { ...value, value: e.target.value },
              },
            })
            onChange(e)
          }}
          error={fieldState.invalid}
        />
      )}
    </>
  )
}
